<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
  >
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>
    <div class="insurance-prescription">
      <h2
        class="insurance-prescription__header"
        v-html="$t('eu__insurance_prescription__private__header')"
      />
      <img
        alt="TeleClinic Logo"
        class="insurance-prescription__banner"
        src="@eu/app/assets/img/tele_clinic_appointment.svg"
      />

      <div class="insurance-prescription__info">
        <div
          class="insurance-prescription__description"
          v-html="
            $t('eu__insurance_prescription__private__description__introduction')
          "
        />
        <div
          class="insurance-prescription__description insurance-prescription__description--strong"
          v-html="costsText"
        />
        <div
          class="insurance-prescription__description"
          v-html="
            $t('eu__insurance_prescription__private__description__details')
          "
        />
      </div>
    </div>
    <template #actions>
      <BaseButton
        :text="$t('eu__insurance_prescription__private__cta')"
        data-qa="insurance-prescription-cta"
        @click="subscribeForKaia"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import BaseButton from '@shared/components/BaseButton.vue'
import env from '@shared/env.js'
import ButtonBack from '@shared/components/ButtonBack.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'

const { t } = useI18n()
const { navigateBack, canGoBack } = useNavigation(
  ROUTE.INSURANCE_PRESCRIPTION_PRIVATE,
)
const { progress } = useOnboardingProgress()

const appName = computed(() => t('eu__generic__app'))
const costsText = computed(() =>
  t('eu__insurance_prescription__private__description__costs', {
    app_name: appName.value,
  }),
)

const subscribeForKaia = () => {
  const emailSubject = t('eu__insurance_prescription__private__email_subject', {
    app_name: appName.value,
  })
  const emailBody = `${t('eu__insurance_prescription__private__email_body', {
    app_name: appName.value,
  })}\n\n\n\n\n<html></html>`
  const link = document.createElement('a')
  link.setAttribute(
    'href',
    [
      `mailto:${env.VITE_SUPPORT_EMAIL}`,
      '?',
      `subject=${encodeURIComponent(emailSubject)}`,
      '&',
      `body=${encodeURIComponent(emailBody)}`,
    ].join(''),
  )
  link.click()
}
</script>

<style lang="scss">
.insurance-prescription {
  width: 100%;
  max-width: 784px;
  box-sizing: border-box;

  &__header {
    margin: 0 0 16px;

    @include kds-typography-title-xlarge;
  }

  &__description {
    @include kds-typography-paragraph-large;
    color: $kds-color-text-secondary;
    margin: 16px 0;

    &--strong {
      font-weight: 700;
      color: $kds-color-text-primary;
      text-align: center;
      margin: 32px 0;
    }
  }

  &__banner {
    display: block;
    width: 100%;
    margin-bottom: 16px;
  }

  &__info {
    padding-bottom: 32px;
  }

  &__logo {
    max-height: 35px;
    max-width: 187px;
  }

  &-guidance {
    @include kds-typography-paragraph-large;

    &__title {
      color: $kds-color-text-tertiary;
      margin: 24px 0 4px;
      font-weight: 500;
    }

    &__list {
      margin: 0;
      color: $kds-color-text-tertiary;
      padding-left: 16px;
    }

    &__item {
      margin-bottom: 4px;
    }

    &__notification {
      padding: 8px 32px;
      height: 58px;
      box-sizing: border-box;
      text-align: center;
      font-weight: 500;

      &:before {
        content: '';
        display: inline-block;
        vertical-align: middle;
        height: 100%;
      }
    }
  }

  &__pdf {
    display: block;
    width: 100%;
    min-height: 480px;
    margin: 32px 0;
    border: 2px solid $kds-color-background-accent;
  }

  @include media-up(md) {
    &__description {
      &--strong {
        @include kds-typography-title-large;
        max-width: 70%;
        margin: 32px auto;
      }
    }

    &__logo {
      max-height: 70px;
    }

    &-guidance {
      &__notification {
        width: 50%;
      }
    }

    &__pdf {
      width: 100%;
      min-height: 640px;
    }
  }
}
</style>
